import React, { useState, useEffect, useRef } from 'react';
import { whatsappService } from '../services/whatsappService';
import { toast } from 'react-toastify';
import LoadingOverlay from '../components/LoadingOverlay';
import './WhatsAppConfig.css';

const WhatsAppConfig = () => {
  const [config, setConfig] = useState(null);
  const [status, setStatus] = useState({ connected: false });
  const [qrCode, setQrCode] = useState(null);
  const [loading, setLoading] = useState(true);
  const [initializing, setInitializing] = useState(false);
  const [instanceName, setInstanceName] = useState();
  const [phoneNumber, setPhoneNumber] = useState('');
  const [overlay, setOverlay] = useState({ open: false, message: '' });
  const qrIntervalRef = useRef(null);
  const statusIntervalRef = useRef(null);

  useEffect(() => {
    loadConfig();
    return () => {
      clearInterval(qrIntervalRef.current);
      clearInterval(statusIntervalRef.current);
    };
  }, []);

  const loadConfig = async () => {
    try {
      const [configData, statusData] = await Promise.all([
        whatsappService.getConfig(),
        whatsappService.getStatus().catch(() => ({ connected: false }))
      ]);
      const hasInstance = configData.config && configData.config.instance_id;

      if (hasInstance) {
        setConfig(configData.config);
        setInstanceName(configData.config.instance_name || '');
        setPhoneNumber(configData.config.phone_number || '');
        setStatus(statusData);
      } else {
        // Sem instância: exigir nova inicialização
        setConfig(null);
        setInstanceName('');
        setPhoneNumber('');
        setStatus({ connected: false });
      }
    } catch (error) {
      console.error('Erro ao carregar config:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInitialize = async () => {
    let cleanNumber = phoneNumber?.replace(/\D/g, '') || '';
    if (cleanNumber && cleanNumber.length < 10) {
      toast.error('Número de telefone inválido. Use o formato: 5511999999999');
      return;
    }

    setInitializing(true);
    setOverlay({ open: true, message: 'Inicializando instância do WhatsApp...' });
    try {
      const data = await whatsappService.saveConfig({ 
        instance_name: instanceName || undefined,
        phone_number: cleanNumber || undefined
      });
      setConfig(data.config);
      toast.success('WhatsApp inicializado! Agora conecte seu dispositivo.');
      loadConfig();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao inicializar WhatsApp');
    } finally {
      setInitializing(false);
      setOverlay({ open: false, message: '' });
    }
  };

  const handleGenerateQR = async () => {
    const fetchQR = async () => {
      try {
        const data = await whatsappService.generateQRCode();

        if (data.qrCode) {
          setQrCode(data.qrCode);
          toast.info('Escaneie o QR Code com seu WhatsApp');
        } else if (data.alreadyConnected) {
          toast.info(data.message || 'WhatsApp já está conectado');
          setStatus({ connected: true });
          clearInterval(qrIntervalRef.current);
          clearInterval(statusIntervalRef.current);
          setQrCode(null);
        }
      } catch (error) {
        toast.error(error.response?.data?.error || 'Erro ao conectar');
      }
    };

    clearInterval(qrIntervalRef.current);
    clearInterval(statusIntervalRef.current);

    setOverlay({ open: true, message: 'Gerando QR Code...' });
    await fetchQR();
    setOverlay({ open: false, message: '' });

    qrIntervalRef.current = setInterval(fetchQR, 20000);

    statusIntervalRef.current = setInterval(async () => {
      const statusData = await whatsappService.getStatus();
      if (statusData.connected) {
        clearInterval(qrIntervalRef.current);
        clearInterval(statusIntervalRef.current);
        setQrCode(null);
        setStatus(statusData);
        toast.success('WhatsApp conectado!');
      }
    }, 5000);
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Desconectar WhatsApp?')) return;
    try {
      setOverlay({ open: true, message: 'Desconectando WhatsApp...' });
      await whatsappService.disconnect();
      setStatus({ connected: false });
      setQrCode(null);
      clearInterval(qrIntervalRef.current);
      clearInterval(statusIntervalRef.current);
      toast.success('Desconectado!');
    } catch (error) {
      toast.error('Erro ao desconectar');
    } finally {
      setOverlay({ open: false, message: '' });
    }
  };

  const handleResetConfig = async () => {
    if (!window.confirm('Remover configuração e reiniciar?')) return;
    try {
      setOverlay({ open: true, message: 'Removendo configuração...' });
      // Tenta desconectar, mas segue mesmo se falhar
      await whatsappService.disconnect().catch(() => {});
      setConfig(null);
      setStatus({ connected: false });
      setQrCode(null);
      setPhoneNumber('');
      setInstanceName('');
      clearInterval(qrIntervalRef.current);
      clearInterval(statusIntervalRef.current);
      toast.success('Configuração removida. Reconfigure para continuar.');
    } catch (error) {
      toast.error('Erro ao remover configuração');
    } finally {
      setOverlay({ open: false, message: '' });
    }
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="whatsapp-config">
      {overlay.open && <LoadingOverlay message={overlay.message} />}
      <div className="container">
        <h1>Configuração WhatsApp</h1>
        <p style={{ marginBottom: '2rem', color: 'var(--text-light)' }}>
          Configure a integração com Evolution API para enviar lembretes via WhatsApp
        </p>

        <div style={{ maxWidth: '600px', margin: '0 auto' }}>
          <div className="card">
            <h2>Status da Instância</h2>
            
            {!config && (
              <div style={{ textAlign: 'center', padding: '2rem' }}>
                <p style={{ marginBottom: '1.5rem', color: 'var(--text-light)' }}>
                  Configure seu número de telefone para conectar via Evolution API
                </p>
                
                <div className="form-group" style={{ textAlign: 'left', marginBottom: '1.5rem' }}>
                  <label className="form-label">Número de Telefone *</label>
                  <input
                    type="text"
                    className="form-input"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    placeholder="5511999999999"
                  />
                  <small style={{ color: 'var(--text-light)', display: 'block', marginTop: '0.5rem' }}>
                    Use o formato: DDI + DDD + Número (ex: 5511999999999)
                  </small>
                </div>
                
                <button 
                  onClick={handleInitialize} 
                  className="btn btn-primary"
                  disabled={initializing}
                >
                  {initializing ? 'Inicializando...' : '🚀 Inicializar WhatsApp'}
                </button>
              </div>
            )}

            {config && (
              <>
                <div style={{ padding: '1rem', background: 'var(--light-color)', borderRadius: '0.5rem', marginBottom: '1rem' }}>
                  {config.phone_number && (
                    <p style={{ fontSize: '0.875rem', color: 'var(--text-light)', marginBottom: '0.5rem' }}>
                      <strong>Telefone:</strong> +{config.phone_number}
                    </p>
                  )}
                  {config.instance_name && (
                    <p style={{ fontSize: '0.875rem', color: 'var(--text-light)', marginBottom: '0.5rem' }}>
                      <strong>Nome:</strong> {config.instance_name}
                    </p>
                  )}
                  <p style={{ fontSize: '0.875rem', color: 'var(--text-light)' }}>
                    <strong>Instance ID:</strong> {config.instance_id}
                  </p>
                </div>

                <div className="connection-status">
                  <div className={`status-indicator ${status.connected ? 'connected' : 'disconnected'}`}>
                    {status.connected ? '✓ Conectado' : '✗ Desconectado'}
                  </div>
                </div>

                {!status.connected && (
                  <div style={{ textAlign: 'center', marginTop: '2rem', display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
                    {!qrCode && (
                      <button onClick={handleGenerateQR} className="btn btn-success">
                        Conectar WhatsApp
                      </button>
                    )}
                    <button onClick={handleResetConfig} className="btn btn-outline">
                      Remover configuração
                    </button>
                  </div>
                )}

                {status.connected && (
                  <div style={{ textAlign: 'center', marginTop: '2rem' }}>
                    <button onClick={handleDisconnect} className="btn btn-danger">
                      Desconectar
                    </button>
                  </div>
                )}
              </>
            )}

            {qrCode && (
              <div className="qr-code-container" style={{ marginTop: '2rem' }}>
                <h3>Escaneie com WhatsApp</h3>
                <p style={{ marginBottom: '1rem', color: 'var(--text-light)' }}>
                  Abra o WhatsApp no seu celular e escaneie este código
                </p>
                <img
                  src={qrCode.startsWith('data:') ? qrCode : `data:image/png;base64,${qrCode}`}
                  alt="QR Code"
                  style={{ width: 256, height: 256 }}
                />
              </div>
            )}

          </div>
        </div>

        <div className="card" style={{ marginTop: '2rem' }}>
          <h2>ℹ️ Instruções</h2>
          <ol style={{ paddingLeft: '1.5rem', lineHeight: '1.8' }}>
            <li>Informe (opcional) seu número de telefone no formato internacional (ex: 5511999999999)</li>
            <li>Clique em "Inicializar WhatsApp" para criar sua instância</li>
            <li>Clique em "Conectar WhatsApp" para gerar o QR Code</li>
            <li>O QR Code atualiza automaticamente a cada 20s e some ao conectar</li>
            <li>Abra o WhatsApp no celular → Menu → Aparelhos conectados → Conectar aparelho</li>
            <li>Escaneie o QR Code exibido na tela</li>
            <li>Em caso de problemas, use "Remover configuração" e reconfigure</li>
          </ol>
        </div>
        </div>
    </div>
  );
};

export default WhatsAppConfig;
